import type { ConfigObjectMatch, ContentUpdateResult } from '@cli-types/next-adapter/init.js'
import { BETTERSTART_ASSET_HOST } from '@cli-constants/scaffolding.js'
import { findMatchingBrace, findMatchingBracket, getLineIndent } from '@cli-utils/js-source-scan.js'
import { detectPropertyIndent } from './detect-property-indent.js'
import { findTopLevelProperty } from './find-top-level-property.js'
import { hasBetterStartImageHostConfigured } from './has-better-start-image-host-configured.js'

export function ensureBetterStartImageHost(
  content: string,
  configObject: ConfigObjectMatch
): ContentUpdateResult {
  const imagesProp = findTopLevelProperty(content, configObject.start, configObject.end, 'images')

  if (!imagesProp) {
    return {
      content:
        content.slice(0, configObject.start + 1) +
        `\n${configObject.propertyIndent}images: {\n${configObject.propertyIndent}  remotePatterns: [\n${configObject.propertyIndent}    {\n${configObject.propertyIndent}      protocol: 'https',\n${configObject.propertyIndent}      hostname: '${BETTERSTART_ASSET_HOST}',\n${configObject.propertyIndent}    },\n${configObject.propertyIndent}  ],\n${configObject.propertyIndent}},` +
        content.slice(configObject.start + 1),
      changed: true
    }
  }

  const imagesValue = imagesProp.value.trim()
  if (!imagesValue.startsWith('{')) {
    return { content, changed: false, unsupported: true }
  }

  const imagesObjectStart = content.indexOf('{', imagesProp.valueStart)
  if (imagesObjectStart < 0) {
    return { content, changed: false, unsupported: true }
  }

  const imagesObjectEnd = findMatchingBrace(content, imagesObjectStart)
  if (imagesObjectEnd < 0) {
    return { content, changed: false, unsupported: true }
  }

  if (hasBetterStartImageHostConfigured(content, imagesObjectStart, imagesObjectEnd)) {
    return { content, changed: false }
  }

  const remotePatternsProp = findTopLevelProperty(
    content,
    imagesObjectStart,
    imagesObjectEnd,
    'remotePatterns'
  )
  if (!remotePatternsProp) {
    const propertyIndent = detectPropertyIndent(content, imagesObjectStart)
    return {
      content:
        content.slice(0, imagesObjectStart + 1) +
        `\n${propertyIndent}remotePatterns: [\n${propertyIndent}  {\n${propertyIndent}    protocol: 'https',\n${propertyIndent}    hostname: '${BETTERSTART_ASSET_HOST}',\n${propertyIndent}  },\n${propertyIndent}],` +
        content.slice(imagesObjectStart + 1),
      changed: true
    }
  }

  const remotePatternsValue = remotePatternsProp.value.trim()
  if (!remotePatternsValue.startsWith('[')) {
    return { content, changed: false, unsupported: true }
  }

  const arrayStart = content.indexOf('[', remotePatternsProp.valueStart)
  if (arrayStart < 0) {
    return { content, changed: false, unsupported: true }
  }

  const arrayEnd = findMatchingBracket(content, arrayStart)
  if (arrayEnd < 0) {
    return { content, changed: false, unsupported: true }
  }

  if (content.slice(arrayStart, arrayEnd + 1).includes(BETTERSTART_ASSET_HOST)) {
    return { content, changed: false }
  }

  const arrayIndent = detectPropertyIndent(content, arrayStart)
  const closingIndent = getLineIndent(content, arrayStart)
  const beforeClose = content.slice(arrayStart + 1, arrayEnd)
  const trailingWhitespace = beforeClose.match(/\s*$/)?.[0] ?? ''
  const insertionPoint = arrayEnd - trailingWhitespace.length
  const normalizedItems = beforeClose.slice(0, beforeClose.length - trailingWhitespace.length)
  const separator = normalizedItems.trim().length === 0 || /,\s*$/.test(normalizedItems) ? '' : ','
  const closingGap = trailingWhitespace.length > 0 ? trailingWhitespace : `\n${closingIndent}`

  return {
    content:
      content.slice(0, insertionPoint) +
      `${separator}\n${arrayIndent}{\n${arrayIndent}  protocol: 'https',\n${arrayIndent}  hostname: '${BETTERSTART_ASSET_HOST}',\n${arrayIndent}},${closingGap}` +
      content.slice(arrayEnd),
    changed: true
  }
}
